import { EYE_DISTANCE, eyeDrift, restingEye } from "./scene/eye";
import type { ThemeId } from "./shelf-look";

/**
 * The HTML side of each theme: the page around the scene, the fallback drawn
 * before it is ready, and the text over it.
 */
export type ThemeStyle = {
  /** The case is a walnut cabinet with a frame, rather than shelves on a wall. */
  cabinet: boolean;
  /** Books lie in piles on a desk, spine out, rather than standing in rows. */
  piles: boolean;
  wall: string;
  shelf: string;
  /** The HTML cabinet's timber and the wash over its back panel, shown until the scene is ready. */
  timber: string;
  backing: string;
  /** Thickness of a floating shelf, in page pixels; the classic case has planks instead. */
  shelfThickness: number;
  /** Text colour over the room. */
  ink: string;
  /** Classes for the section labels on the shelf edges. */
  label: string;
  /** Classes for the dialog showing a book, and the text in it. */
  surface: string;
  muted: string;
  font: "serif" | "sans";
};

const WALNUT = "url(/bookshelf/walnut.webp)";
const NO_CABINET = { timber: "none", backing: "none" };

export const THEME_STYLES = {
  sunlit: {
    shelfThickness: 0,
    cabinet: true,
    piles: false,
    wall: "#f1e4d2",
    shelf: "#c6965a",
    timber: "linear-gradient(#cf9f63, #bb8a4f)",
    backing: "linear-gradient(#c9b08f, #dcc6a7)",
    ink: "#2a1c13",
    label: "sr-only",
    surface: "bg-[#f4eadc]/80 text-[#2a1c13]",
    muted: "text-[#2a1c13]/55",
    font: "serif",
  },
  classic: {
    shelfThickness: 0,
    cabinet: true,
    piles: false,
    wall: "#1b1310",
    shelf: "#5a3b28",
    timber: WALNUT,
    backing: `linear-gradient(rgb(14 9 6 / 0.74), rgb(14 9 6 / 0.74)), ${WALNUT}`,
    ink: "#f1e8d8",
    label:
      "rounded-[2px] bg-[linear-gradient(180deg,#f3dc9a,#c99a45_55%,#9c7328)] px-1.5 text-[10.5px] font-semibold leading-[13px] tracking-[0.12em] text-[#3a2710] uppercase shadow-[0_1px_1px_rgb(0_0_0/0.6),inset_0_1px_0_rgb(255_250_225/0.7)]",
    surface: "bg-[#0e0b09]/50 text-[#f1e8d8]",
    muted: "text-[#f1e8d8]/55",
    font: "serif",
  },
  gallery: {
    shelfThickness: 12,
    cabinet: false,
    piles: false,
    wall: "#e8e3db",
    shelf: "#f1eee8",
    ...NO_CABINET,
    ink: "#1d1b18",
    label: "text-[11px] font-medium leading-[14px] tracking-[0.08em] text-[#1d1b18]/60 uppercase",
    surface: "bg-[#f3f0ea]/80 text-[#1d1b18]",
    muted: "text-[#1d1b18]/55",
    font: "sans",
  },
  studio: {
    shelfThickness: 6,
    cabinet: false,
    piles: false,
    wall: "#16171a",
    shelf: "#101012",
    ...NO_CABINET,
    ink: "#ecebe8",
    label: "text-[11px] font-medium leading-[14px] tracking-[0.08em] text-[#ecebe8]/55 uppercase",
    surface: "bg-[#0b0b0c]/70 text-[#ecebe8]",
    muted: "text-[#ecebe8]/50",
    font: "sans",
  },
  modern: {
    shelfThickness: 18,
    cabinet: false,
    piles: false,
    wall: "#d8d0c4",
    shelf: "#b58b5c",
    ...NO_CABINET,
    ink: "#221d17",
    label: "text-[11px] font-medium leading-[14px] tracking-[0.08em] text-[#221d17]/60 uppercase",
    surface: "bg-[#ece6dc]/80 text-[#221d17]",
    muted: "text-[#221d17]/55",
    font: "sans",
  },
  desk: {
    // The shelves on a narrow screen, where the piles take more than one row.
    shelfThickness: 18,
    cabinet: false,
    piles: true,
    wall: "#e7e2da",
    shelf: "#d9bb91",
    ...NO_CABINET,
    ink: "#231e18",
    label: "text-[11px] font-medium leading-[14px] tracking-[0.08em] text-[#231e18]/60 uppercase",
    surface: "bg-[#efe9df]/80 text-[#231e18]",
    muted: "text-[#231e18]/55",
    font: "sans",
  },
} satisfies Record<ThemeId, ThemeStyle>;

/**
 * Room kept clear at the right of the desk for the lamp and the mug, in page
 * pixels, on screens wide enough for them. The piles stay out of it.
 */
export const DESK_CORNER = 280;
export const DESK_CORNER_MIN_WIDTH = 900;

/** A camera looking at the viewport from where the eye is when the page is drawn in perspective. */
export type Camera = {
  /** In viewport pixels. */
  eye: { x: number; y: number };
  distance: number;
};

/** The camera over the bookcase `container` right now, in viewport pixels, wherever the eye has wandered. */
export function shelfCamera(container: Element, theme: ThemeId): Camera {
  const { left, width } = container.getBoundingClientRect();
  const rest = restingEye(theme);
  return {
    eye: { x: left + width / 2 + eyeDrift.x, y: rest.y + eyeDrift.y },
    distance: EYE_DISTANCE * window.innerHeight,
  };
}
